import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Lock } from 'lucide-react';

const LOGO_URL = "/lane4-logo.png";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/programs' },
    { name: 'Impact', path: '/impact' },
    { name: 'Events', path: '/events' },
    { name: 'Sponsors', path: '/sponsors' },
    { name: 'Get Involved', path: '/get-involved' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white border-b border-slate-100 shadow-sm' : 'bg-slate-900'
      }`}
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="Lane 4 For All Kids Foundation"
              className="h-16 w-auto bg-white rounded-md p-1.5"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive(link.path)
                    ? 'text-red-600'
                    : isScrolled
                    ? 'text-slate-900 hover:text-red-600'
                    : 'text-white hover:text-red-400'
                }`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* Admin Login + Donate Button */}
          <div className="hidden lg:flex items-center space-x-5">
            <Link
              to="/admin"
              className={`flex items-center text-xs font-medium uppercase tracking-wider transition-colors ${
                isScrolled ? 'text-slate-500 hover:text-red-600' : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-admin-login"
            >
              <Lock className="w-3.5 h-3.5 mr-1.5" />
              Login
            </Link>
            <Link
              to="/donate"
              className="bg-red-600 text-white px-6 py-3 font-bold uppercase tracking-widest text-sm hover:bg-slate-900 transition-colors"
              data-testid="nav-donate-btn"
            >
              Donate Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-900' : 'text-white'}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden fixed inset-0 top-20 bg-white z-40 transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        data-testid="mobile-menu"
      >
        <nav className="flex flex-col p-6 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-lg font-medium uppercase tracking-wider py-2 border-b border-slate-100 ${
                isActive(link.path) ? 'text-red-600' : 'text-slate-900'
              }`}
              data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.name}
            </Link>
          ))}
          <Link
            to="/donate"
            className="bg-red-600 text-white px-6 py-4 font-bold uppercase tracking-widest text-center mt-4"
            data-testid="mobile-donate-btn"
          >
            Donate Now
          </Link>
          <Link
            to="/admin"
            className="flex items-center justify-center text-sm font-medium uppercase tracking-wider text-slate-500 py-2"
            data-testid="mobile-nav-admin-login"
          >
            <Lock className="w-4 h-4 mr-2" />
            Admin Login
          </Link>
        </nav>
      </div>
    </header>
  );
};

export default Navigation;
